import { Box, Menu, MenuItem, TextField } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import GroupsIcon from '@mui/icons-material/Groups'
import ThermostatIcon from '@mui/icons-material/Thermostat'
import RuleIcon from '@mui/icons-material/Rule'
import SyncIcon from '@mui/icons-material/Sync'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ScheduleIcon from '@mui/icons-material/Schedule'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek'
import WaterDropIcon from '@mui/icons-material/WaterDrop'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SaveIcon from '@mui/icons-material/Save'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import BarChartIcon from '@mui/icons-material/BarChart'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import CategoryIcon from '@mui/icons-material/Category'
import BlenderIcon from '@mui/icons-material/Blender'

import {
  useScheduleProductionRequirements,
  useUpdateProductionRequirements,
} from './Scheduler.data'
import {
  SynchronizedLinesRowType,
  SteriliserRoutingRowType,
  SchedulerResourceAndMaterialConstraintsRowType,
} from './Scheduler.types'
import {
  Band,
  CapMeter,
  ConflictMark,
  DayHeader,
  LineToken,
  MODE_COLOR,
  MONO,
  PoolSpark,
  RuleTabs,
  Stat,
  Stepper,
  TOKENS,
  WeekTrack,
  WorkbenchTab,
  Window,
  concurrency,
  cyclesFor,
  joinLines,
  joinPeriods,
  makePool,
  overrunHours,
  parseLines,
  parsePeriods,
  PeriodNum,
  poolMin,
  pct,
  resUsage,
  synthActivity,
  synthTempSegments,
} from './RulesWorkbench.timeline'

const SYNC_API = 'synchronized-lines'
const STER_TYPES_API = 'steriliser-types'
const STER_ROUTING_API = 'steriliser-routing'
const CONSTRAINTS_API = 'resource-and-material-constraints'

const SYNC_GROUP_MODES = ['132°C', '135°C', '138°C', '141°C']

type InitialTab = 'crew' | 'sync' | 'steriliser' | 'resources'

interface RulesWorkbenchProps {
  schedulerId: string
  iteration: string
  initialTab?: InitialTab
}

const toTab = (t: InitialTab | undefined): WorkbenchTab =>
  t === 'steriliser'
    ? 'ster'
    : t === 'resources'
      ? 'res'
      : t === 'sync'
        ? 'sync'
        : 'crew'

// ---- view models -----------------------------------------------------------

interface CrewVM {
  id: number
  groupName: string
  active: number
  lines: string[]
  periods: Window[]
  changeoverTime: number
}
interface SyncVM {
  id: number
  groupName: string
  changeoverTime: number
  lines: string[]
  mode: string
}
interface SterVM {
  id: number
  steriliserTypeId: string
  basketsPerCycle: number
  cycleHours: number
  instanceCount: number
  family: string
  lines: string[]
  routingId: number | null
}
interface BasketFamily {
  name: string
  cap: number
  openingFreeBaskets: number
  fullBaskets: number
}
interface ResVM {
  id: number
  resource: string
  amount: number
  uom: string
  scope: string
  consumers: number
}

const num = (v: unknown, d = 0): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v))
  return isNaN(n) ? d : n
}

// fieldset label
const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      fontSize: 9.5,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: TOKENS.ink400,
      mb: '6px',
    }}
  >
    {children}
  </Box>
)

const PanelCard = ({
  title,
  icon,
  right,
  children,
}: {
  title: string
  icon: React.ReactNode
  right?: React.ReactNode
  children: React.ReactNode
}) => (
  <Box
    sx={{
      border: `1px solid ${TOKENS.line200}`,
      borderRadius: '4px',
      p: '12px 14px',
      background: TOKENS.panel,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px' }}>
      {icon}
      <Box
        component="span"
        sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.ink700 }}
      >
        {title}
      </Box>
      <Box sx={{ flex: 1 }} />
      {right}
    </Box>
    {children}
  </Box>
)

const AddLineMenu = ({
  avail,
  onAdd,
}: {
  avail: string[]
  onAdd: (l: string) => void
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  if (!avail.length) return null
  return (
    <>
      <Box
        component="button"
        onClick={(e: React.MouseEvent<HTMLElement>) =>
          setAnchor(e.currentTarget)
        }
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          height: 24,
          px: '9px',
          borderRadius: '3px',
          border: `1px dashed ${TOKENS.line300}`,
          background: 'transparent',
          color: TOKENS.ink400,
          cursor: 'pointer',
          fontSize: 11.5,
          fontWeight: 600,
          fontFamily: 'inherit',
          '& svg': { fontSize: 13 },
        }}
      >
        <AddIcon />
        Add line
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {avail.map((l) => (
          <MenuItem
            key={l}
            dense
            onClick={() => {
              onAdd(l)
              setAnchor(null)
            }}
            sx={{ fontFamily: MONO, fontSize: 12 }}
          >
            {l}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const okNote = (msg: string) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: 11.5,
      color: TOKENS.stateComplete,
      fontWeight: 600,
    }}
  >
    <CheckCircleIcon sx={{ fontSize: 14 }} />
    {msg}
  </Box>
)
const warnNote = (msg: string) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: 11.5,
      color: TOKENS.ganttDowntime,
      fontWeight: 600,
    }}
  >
    <WarningAmberIcon sx={{ fontSize: 14 }} />
    {msg}
  </Box>
)
const infoNote = (msg: string) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: 11.5,
      color: TOKENS.ink500,
      fontWeight: 600,
    }}
  >
    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
    {msg}
  </Box>
)

export const RulesWorkbench = ({
  schedulerId,
  iteration,
  initialTab,
}: RulesWorkbenchProps) => {
  const [tab, setTab] = useState<WorkbenchTab>(toTab(initialTab))

  // ---- data ----------------------------------------------------------------
  const { data: syncResp = { data: [], availableLines: [] } } =
    useScheduleProductionRequirements(schedulerId, SYNC_API, iteration)
  const syncRows = (syncResp.data ?? []) as SynchronizedLinesRowType[]
  const availableLines: string[] = (syncResp.availableLines ?? []) as string[]

  const { data: sterTypesResp = { data: [] } } =
    useScheduleProductionRequirements(schedulerId, STER_TYPES_API, iteration)
  const sterTypeRows = (sterTypesResp.data ?? []) as Record<string, unknown>[]

  const { data: sterRoutingResp = { data: [] } } =
    useScheduleProductionRequirements(schedulerId, STER_ROUTING_API, iteration)
  const routingRows = (sterRoutingResp.data ?? []) as SteriliserRoutingRowType[]

  const { data: constraintsResp = { data: [] } } =
    useScheduleProductionRequirements(schedulerId, CONSTRAINTS_API, iteration)
  const constraintRows = (constraintsResp.data ??
    []) as SchedulerResourceAndMaterialConstraintsRowType[]

  // ---- derive view models --------------------------------------------------
  const crewVMs: CrewVM[] = useMemo(
    () =>
      syncRows
        .filter((r) => String(r.productMasterType).toLowerCase() === 'active')
        .map((r) => ({
          id: r.id,
          groupName: r.groupName,
          active: num(r.active, 1),
          lines: parseLines(r.lines),
          periods: parsePeriods(r.periods),
          changeoverTime: num(r.changeoverTime),
        })),
    [syncRows]
  )

  const syncVMs: SyncVM[] = useMemo(
    () =>
      syncRows
        .filter(
          (r) => String(r.productMasterType).toLowerCase() === 'temperature'
        )
        .map((r, i) => ({
          id: r.id,
          groupName: r.groupName,
          changeoverTime: num(r.changeoverTime),
          lines: parseLines(r.lines),
          mode: SYNC_GROUP_MODES[i % SYNC_GROUP_MODES.length],
        })),
    [syncRows]
  )

  const basketFamilies: BasketFamily[] = useMemo(() => {
    const fams = constraintRows
      .filter(
        (r) =>
          String((r as Record<string, unknown>).ConstraintType) ===
          'basket_pool'
      )
      .map((r) => {
        const rec = r as Record<string, unknown>
        return {
          name: r.ResourceConstraint,
          cap: num(r.ConstraintAmount, 24),
          openingFreeBaskets: num(
            rec.openingFreeBaskets,
            num(r.ConstraintAmount, 24)
          ),
          fullBaskets: num(rec.fullBaskets, 0),
        }
      })
    return fams.length
      ? fams
      : [{ name: 'Family A', cap: 24, openingFreeBaskets: 20, fullBaskets: 0 }]
  }, [constraintRows])

  const sterVMs: SterVM[] = useMemo(
    () =>
      sterTypeRows.map((t) => {
        const typeId = String(t.steriliserTypeId)
        const routing = routingRows.find((x) => x.steriliserTypeId === typeId)
        return {
          id: num(t.id),
          steriliserTypeId: typeId,
          basketsPerCycle: num(t.basketsPerCycle, 1),
          cycleHours: num(t.cycleHours, 1),
          instanceCount: num(t.instanceCount, 1),
          family: routing?.basketFamilyId ?? basketFamilies[0]?.name ?? '',
          lines: parseLines(routing?.lines),
          routingId: routing ? routing.id : null,
        }
      }),
    [sterTypeRows, routingRows, basketFamilies]
  )

  const resVMs: ResVM[] = useMemo(
    () =>
      constraintRows
        .filter(
          (r) =>
            String((r as Record<string, unknown>).ConstraintType) !==
            'basket_pool'
        )
        .map((r) => ({
          id: r.id,
          resource: r.ResourceConstraint,
          amount: num(r.ConstraintAmount),
          uom: r.UOM ?? 'units/h',
          scope:
            String((r as Record<string, unknown>).ConstraintType ?? 'Site') ||
            'Site',
          // consumers is not a persisted field — derived illustration.
          consumers: 2 + (Math.abs(r.id) % 7),
        })),
    [constraintRows]
  )

  // ---- selection + working draft ------------------------------------------
  const [selId, setSelId] = useState<Record<WorkbenchTab, number | null>>({
    crew: null,
    sync: null,
    ster: null,
    res: null,
  })
  const [draft, setDraft] = useState<CrewVM | SyncVM | SterVM | ResVM | null>(
    null
  )
  const [draftKey, setDraftKey] = useState<string>('')

  const listForTab = (t: WorkbenchTab): { id: number }[] =>
    t === 'crew'
      ? crewVMs
      : t === 'sync'
        ? syncVMs
        : t === 'ster'
          ? sterVMs
          : resVMs

  // resolve the effective selected id for the active tab (default to first)
  const activeList = listForTab(tab)
  const effSelId = selId[tab] ?? (activeList.length ? activeList[0].id : null)

  // seed / reseed the draft when the selected rule (or its server data) changes
  useEffect(() => {
    if (effSelId == null) {
      setDraft(null)
      setDraftKey('')
      return
    }
    const key = `${tab}:${effSelId}`
    const source =
      tab === 'crew'
        ? crewVMs.find((x) => x.id === effSelId)
        : tab === 'sync'
          ? syncVMs.find((x) => x.id === effSelId)
          : tab === 'ster'
            ? sterVMs.find((x) => x.id === effSelId)
            : resVMs.find((x) => x.id === effSelId)
    // Only reseed when switching rule/tab — preserve in-progress edits across refetches.
    if (source && key !== draftKey) {
      setDraft(JSON.parse(JSON.stringify(source)))
      setDraftKey(key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, effSelId, crewVMs, syncVMs, sterVMs, resVMs])

  const pick = (id: number) => setSelId((s) => ({ ...s, [tab]: id }))

  const update = (patch: Record<string, unknown>) =>
    setDraft((d) => (d ? ({ ...d, ...patch } as typeof d) : d))

  // ---- save ----------------------------------------------------------------
  const updateMut = useUpdateProductionRequirements(
    () => {},
    () => {}
  )
  const isSaving = updateMut.isLoading

  const handleCancel = () => {
    // force reseed from server on next effect run
    setDraftKey('')
  }

  const handleSave = async () => {
    if (!draft) return
    try {
      const saves: Promise<unknown>[] = []
      if (tab === 'crew' || tab === 'sync') {
        const orig = syncRows.find((r) => r.id === draft.id)
        if (orig) {
          const merged: SynchronizedLinesRowType =
            tab === 'crew'
              ? {
                  ...orig,
                  groupName: (draft as CrewVM).groupName,
                  active: (draft as CrewVM).active,
                  lines: joinLines((draft as CrewVM).lines),
                  periods: joinPeriods((draft as CrewVM).periods),
                  changeoverTime: (draft as CrewVM).changeoverTime,
                }
              : {
                  ...orig,
                  groupName: (draft as SyncVM).groupName,
                  changeoverTime: (draft as SyncVM).changeoverTime,
                  lines: joinLines((draft as SyncVM).lines),
                }
          saves.push(
            updateMut.mutateAsync({
              data: { [draft.id]: merged } as Record<number, unknown>,
              schedulerId,
              API: SYNC_API,
              iteration,
            } as never)
          )
        }
      } else if (tab === 'ster') {
        const d = draft as SterVM
        const origType = sterTypeRows.find((r) => num(r.id) === d.id)
        if (origType) {
          saves.push(
            updateMut.mutateAsync({
              data: {
                [d.id]: {
                  ...origType,
                  basketsPerCycle: d.basketsPerCycle,
                  cycleHours: d.cycleHours,
                  instanceCount: d.instanceCount,
                },
              } as Record<number, unknown>,
              schedulerId,
              API: STER_TYPES_API,
              iteration,
            } as never)
          )
        }
        if (d.routingId != null) {
          const origRouting = routingRows.find((r) => r.id === d.routingId)
          if (origRouting) {
            saves.push(
              updateMut.mutateAsync({
                data: {
                  [d.routingId]: {
                    ...origRouting,
                    lines: joinLines(d.lines),
                    basketFamilyId: d.family,
                  },
                } as Record<number, unknown>,
                schedulerId,
                API: STER_ROUTING_API,
                iteration,
              } as never)
            )
          }
        }
      } else {
        const d = draft as ResVM
        const orig = constraintRows.find((r) => r.id === d.id)
        if (orig) {
          saves.push(
            updateMut.mutateAsync({
              data: {
                [d.id]: { ...orig, ConstraintAmount: d.amount, UOM: d.uom },
              } as Record<number, unknown>,
              schedulerId,
              API: CONSTRAINTS_API,
              iteration,
            } as never)
          )
        }
      }
      if (!saves.length) {
        toast.error('Nothing to save')
        return
      }
      await Promise.all(saves)
      toast.success('Saved successfully')
    } catch {
      toast.error('Failed to save')
    }
  }

  // ---- header tab definitions ---------------------------------------------
  const tabs: {
    id: WorkbenchTab
    label: string
    icon: React.ReactNode
    count: number
  }[] = [
    {
      id: 'sync',
      label: 'Synchronisation',
      icon: <SyncIcon />,
      count: syncVMs.length,
    },
    { id: 'crew', label: 'Crew', icon: <GroupsIcon />, count: crewVMs.length },
    {
      id: 'ster',
      label: 'Steriliser',
      icon: <ThermostatIcon />,
      count: sterVMs.length,
    },
    { id: 'res', label: 'Resources', icon: <RuleIcon />, count: resVMs.length },
  ]
  const TAB_META: Record<
    WorkbenchTab,
    { title: string; sub: string; listTitle: string }
  > = {
    sync: {
      title: 'Synchronisation groups',
      sub: 'Lines that must share equipment state (temperature) at the same time',
      listTitle: 'Sync groups',
    },
    crew: {
      title: 'Crew groups',
      sub: 'Shared operators — a cap on distinct lines touched per period (soft)',
      listTitle: 'Crew groups',
    },
    ster: {
      title: 'Steriliser & basket pools',
      sub: 'Cycle capacity and basket availability across the week',
      listTitle: 'Steriliser types',
    },
    res: {
      title: 'Resource & material constraints',
      sub: 'Shared equipment with an hourly capacity ceiling',
      listTitle: 'Resources',
    },
  }

  // ---- per-tab list + detail + footer -------------------------------------
  let list: React.ReactNode = null
  let detail: React.ReactNode = null
  let footerNote: React.ReactNode = null

  if (tab === 'crew') {
    list = crewVMs.map((c) => {
      const act = synthActivity(c.lines, c.periods)
      const st = concurrency(act, c.lines)
      return (
        <ListItemCard
          key={c.id}
          on={c.id === effSelId}
          onClick={() => pick(c.id)}
          icon={<GroupsIcon sx={{ fontSize: 15 }} />}
          name={c.groupName}
          badge="Crew"
          badgeColor={TOKENS.amberFg}
          meta={`${c.lines.length} lines · max ${c.active}`}
          spark={
            <WeekTrack height={8} radius={2}>
              {c.periods.map((p, j) => (
                <Band
                  key={j}
                  start={p[0]}
                  end={p[1]}
                  color="rgba(224,146,47,0.45)"
                  radius={0}
                  top={0}
                  bottom={0}
                />
              ))}
              {st
                .filter((b) => b.count > c.active)
                .map((b, j) => (
                  <Band
                    key={'o' + j}
                    start={b.start}
                    end={b.end}
                    color={TOKENS.ganttDowntime}
                    radius={0}
                    top={0}
                    bottom={0}
                  />
                ))}
            </WeekTrack>
          }
        />
      )
    })
    const g: CrewVM | null =
      draft != null && 'periods' in draft ? (draft as CrewVM) : null
    if (g) {
      const act = synthActivity(g.lines ?? [], g.periods ?? [])
      const strip = concurrency(act, g.lines)
      const orun = overrunHours(strip, g.active)
      const avail = availableLines.filter((l) => !g.lines.includes(l))
      footerNote =
        orun > 0
          ? warnNote(
              `Crew exceeded in ${orun}h of windows — optimiser penalised (soft)`
            )
          : okNote('Within crew limit across the week')
      detail = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '14px',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ width: 220 }}>
              <FieldLabel>Group name</FieldLabel>
              <TextField
                value={g.groupName}
                onChange={(e) => update({ groupName: e.target.value })}
                size="small"
                fullWidth
                inputProps={{
                  style: {
                    fontSize: 13.5,
                    fontWeight: 600,
                    padding: '5px 10px',
                  },
                }}
              />
            </Box>
            <Stepper
              label="Max active lines"
              value={g.active ?? 1}
              onDec={() => update({ active: Math.max(1, (g.active ?? 1) - 1) })}
              onInc={() => update({ active: (g.active ?? 1) + 1 })}
            />
            <Box sx={{ flex: 1 }} />
            <Stat label="Periods" value={g.periods?.length ?? 0} />
            <Stat
              label="Overrun"
              value={orun}
              unit="h"
              tone={orun > 0 ? TOKENS.ganttDowntime : TOKENS.ink900}
            />
          </Box>

          <Box>
            <FieldLabel>Lines in this crew ({g.lines.length})</FieldLabel>
            <Box
              sx={{
                display: 'flex',
                gap: '6px',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {g.lines.map((l) => (
                <LineToken
                  key={l}
                  name={l}
                  tone="amber"
                  removable
                  onRemove={() =>
                    update({ lines: g.lines.filter((x) => x !== l) })
                  }
                />
              ))}
              <AddLineMenu
                avail={avail}
                onAdd={(l) => update({ lines: [...g.lines, l] })}
              />
            </Box>
          </Box>

          <PanelCard
            title="When the crew is claimed — per line, across the week"
            icon={
              <CalendarViewWeekIcon
                sx={{ fontSize: 15, color: TOKENS.brandTeal }}
              />
            }
            right={
              <Box sx={{ display: 'flex', gap: '10px' }}>
                <LegendDot color={TOKENS.ganttClean} label="active" />
                <LegendDot color={TOKENS.ganttDowntime} label="over max" />
              </Box>
            }
          >
            <DayHeader pad={74} />
            {(g.lines ?? []).map((l) => (
              <Box
                key={l}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  mb: '5px',
                }}
              >
                <Box
                  sx={{
                    width: 66,
                    fontSize: 11,
                    fontFamily: MONO,
                    color: TOKENS.ink600,
                    textAlign: 'right',
                  }}
                >
                  {l}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <WeekTrack height={16}>
                    {(g.periods ?? []).map((p, j) => (
                      <Band
                        key={'p' + j}
                        start={p[0]}
                        end={p[1]}
                        color="rgba(224,146,47,0.10)"
                        radius={0}
                        top={0}
                        bottom={0}
                      />
                    ))}
                    {(act[l] || []).map((w, j) => (
                      <Band
                        key={j}
                        start={w[0]}
                        end={w[1]}
                        color={TOKENS.ganttClean}
                      />
                    ))}
                  </WeekTrack>
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                mt: '9px',
                pt: '9px',
                borderTop: `1px dashed ${TOKENS.line200}`,
              }}
            >
              <Box
                sx={{
                  width: 66,
                  fontSize: 10,
                  fontWeight: 700,
                  color: TOKENS.ink500,
                  textAlign: 'right',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Active
              </Box>
              <Box sx={{ flex: 1 }}>
                <WeekTrack height={20}>
                  {strip.map((b, j) => (
                    <Band
                      key={j}
                      start={b.start}
                      end={b.end}
                      color={
                        b.count > g.active
                          ? TOKENS.ganttDowntime
                          : TOKENS.ganttProduction
                      }
                      label={String(b.count)}
                      radius={0}
                      top={0}
                      bottom={0}
                    />
                  ))}
                </WeekTrack>
              </Box>
            </Box>
          </PanelCard>

          <Box>
            <FieldLabel>Active periods</FieldLabel>
            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {g.periods.map((p, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    p: '6px 9px',
                    border: `1px solid ${TOKENS.line200}`,
                    borderRadius: '3px',
                    background: TOKENS.surface50,
                  }}
                >
                  <ScheduleIcon
                    sx={{ fontSize: 13, color: TOKENS.ganttClean }}
                  />
                  <PeriodNum
                    value={p[0]}
                    onChange={(v) =>
                      update({
                        periods: g.periods.map((q, j) =>
                          j === i ? [v, q[1]] : q
                        ),
                      })
                    }
                  />
                  <ArrowForwardIcon
                    sx={{ fontSize: 12, color: TOKENS.ink300 }}
                  />
                  <PeriodNum
                    value={p[1]}
                    onChange={(v) =>
                      update({
                        periods: g.periods.map((q, j) =>
                          j === i ? [q[0], v] : q
                        ),
                      })
                    }
                  />
                  <CloseIcon
                    sx={{
                      fontSize: 13,
                      color: TOKENS.ink300,
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      update({ periods: g.periods.filter((_, j) => j !== i) })
                    }
                  />
                </Box>
              ))}
              <Box
                component="button"
                onClick={() => update({ periods: [...g.periods, [0, 24]] })}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  height: 30,
                  px: '11px',
                  borderRadius: '3px',
                  border: `1px dashed ${TOKENS.line300}`,
                  background: 'transparent',
                  color: TOKENS.brandTeal,
                  cursor: 'pointer',
                  fontSize: 11.5,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  '& svg': { fontSize: 14 },
                }}
              >
                <AddIcon />
                Add period
              </Box>
            </Box>
          </Box>
        </Box>
      )
    }
  } else if (tab === 'sync') {
    list = syncVMs.map((s) => {
      const { conflicts } = synthTempSegments(s.lines, s.mode ?? '')
      return (
        <ListItemCard
          key={s.id}
          on={s.id === effSelId}
          onClick={() => pick(s.id)}
          icon={<SyncIcon sx={{ fontSize: 15 }} />}
          name={s.groupName}
          badge={s.mode}
          badgeColor={TOKENS.blue600}
          meta={`${s.lines.length} lines · ${s.changeoverTime}h switch`}
          spark={
            <WeekTrack height={8} radius={2}>
              <Band
                start={0}
                end={168}
                color={TOKENS.blue50}
                radius={0}
                top={0}
                bottom={0}
              />
              {conflicts.map((c, j) => (
                <Band
                  key={j}
                  start={c[0]}
                  end={c[1]}
                  color={TOKENS.ganttDowntime}
                  radius={0}
                  top={0}
                  bottom={0}
                />
              ))}
            </WeekTrack>
          }
        />
      )
    })
    const g: SyncVM | null =
      draft != null && 'mode' in draft ? (draft as SyncVM) : null
    if (g) {
      const { segs, conflicts } = synthTempSegments(g.lines, g.mode ?? '')
      const avail = availableLines.filter((l) => !g.lines.includes(l))
      footerNote =
        conflicts.length > 0
          ? warnNote(
              `${conflicts.length} temperature conflict window(s) — lines forced to a shared mode`
            )
          : okNote('No conflicts — group can hold one mode')
      detail = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '14px',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ width: 220 }}>
              <FieldLabel>Group name</FieldLabel>
              <TextField
                value={g.groupName}
                onChange={(e) => update({ groupName: e.target.value })}
                size="small"
                fullWidth
                inputProps={{
                  style: {
                    fontSize: 13.5,
                    fontWeight: 600,
                    padding: '5px 10px',
                  },
                }}
              />
            </Box>
            <Stepper
              label="Changeover (h)"
              value={g.changeoverTime}
              onDec={() =>
                update({
                  changeoverTime: Math.max(
                    0,
                    Math.round((g.changeoverTime - 0.5) * 10) / 10
                  ),
                })
              }
              onInc={() =>
                update({
                  changeoverTime:
                    Math.round((g.changeoverTime + 0.5) * 10) / 10,
                })
              }
            />
            <Box sx={{ flex: 1 }} />
            <Stat label="Lines" value={g.lines.length} />
            <Stat
              label="Conflicts"
              value={conflicts.length}
              tone={conflicts.length ? TOKENS.ganttDowntime : TOKENS.ink900}
            />
          </Box>

          <Box>
            <FieldLabel>Lines that must match ({g.lines.length})</FieldLabel>
            <Box
              sx={{
                display: 'flex',
                gap: '6px',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {g.lines.map((l) => (
                <LineToken
                  key={l}
                  name={l}
                  tone="blue"
                  removable
                  onRemove={() =>
                    update({ lines: g.lines.filter((x) => x !== l) })
                  }
                />
              ))}
              <AddLineMenu
                avail={avail}
                onAdd={(l) => update({ lines: [...g.lines, l] })}
              />
            </Box>
          </Box>

          <PanelCard
            title="Required mode per line — conflicts must resolve to one mode"
            icon={
              <ThermostatIcon sx={{ fontSize: 15, color: TOKENS.brandTeal }} />
            }
          >
            <DayHeader pad={74} />
            {g.lines.map((l) => (
              <Box
                key={l}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  mb: '5px',
                }}
              >
                <Box
                  sx={{
                    width: 66,
                    fontSize: 11,
                    fontFamily: MONO,
                    color: TOKENS.ink600,
                    textAlign: 'right',
                  }}
                >
                  {l}
                </Box>
                <Box sx={{ flex: 1, position: 'relative' }}>
                  <WeekTrack height={16}>
                    {(
                      segs[l] || [[0, 168, (g.mode ?? '').replace('°C', '')]]
                    ).map((s, j) => (
                      <Band
                        key={j}
                        start={s[0]}
                        end={s[1]}
                        color={MODE_COLOR[s[2]] || TOKENS.ganttChangeover}
                        label={s[2] === 'co' ? '' : s[2] + '°'}
                      />
                    ))}
                    {conflicts.map((c, j) => (
                      <ConflictMark key={'c' + j} start={c[0]} end={c[1]} />
                    ))}
                  </WeekTrack>
                </Box>
              </Box>
            ))}
          </PanelCard>
        </Box>
      )
    }
  } else if (tab === 'ster') {
    list = sterVMs.map((s) => (
      <ListItemCard
        key={s.id}
        on={s.id === effSelId}
        onClick={() => pick(s.id)}
        icon={<ThermostatIcon sx={{ fontSize: 15 }} />}
        name={s.steriliserTypeId}
        badge={`×${s.instanceCount}`}
        badgeColor={TOKENS.brandTeal}
        meta={`${s.basketsPerCycle}/cycle · ${s.cycleHours}h · ${s.family}`}
      />
    ))
    const s: SterVM | null =
      draft != null && 'steriliserTypeId' in draft ? (draft as SterVM) : null
    if (s) {
      const fam = basketFamilies.find((f) => f.name === s.family) ||
        basketFamilies[0] || {
          openingFreeBaskets: 20,
          cap: 24,
          fullBaskets: 0,
          name: '',
        }
      const pool = makePool(
        fam.openingFreeBaskets,
        fam.cap,
        s.instanceCount >= 2 ? 1.6 : 2.6,
        Math.max(12, 24 - s.instanceCount * 4),
        s.basketsPerCycle - 1
      )
      const cycles = cyclesFor(
        s.cycleHours,
        Math.max(16, 30 - s.instanceCount * 4)
      )
      const mn = poolMin(pool)
      const avail = availableLines.filter((l) => !s.lines.includes(l))
      footerNote =
        mn <= 3
          ? warnNote(
              `Pool dips to ${mn} baskets — add an instance or shorten the cycle`
            )
          : okNote(`Pool stays above floor (min ${mn})`)
      detail = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '14px',
              flexWrap: 'wrap',
            }}
          >
            <Stepper
              label="Baskets / cycle"
              value={s.basketsPerCycle}
              onDec={() =>
                update({ basketsPerCycle: Math.max(1, s.basketsPerCycle - 1) })
              }
              onInc={() => update({ basketsPerCycle: s.basketsPerCycle + 1 })}
            />
            <Stepper
              label="Cycle time (h)"
              value={s.cycleHours}
              onDec={() =>
                update({ cycleHours: Math.max(1, s.cycleHours - 1) })
              }
              onInc={() => update({ cycleHours: s.cycleHours + 1 })}
            />
            <Stepper
              label="Instances"
              value={s.instanceCount}
              onDec={() =>
                update({ instanceCount: Math.max(1, s.instanceCount - 1) })
              }
              onInc={() => update({ instanceCount: s.instanceCount + 1 })}
            />
            <Box sx={{ flex: 1 }} />
            <Stat label="Routed lines" value={s.lines.length} />
            <Stat
              label="Min free"
              value={mn}
              unit="baskets"
              tone={mn <= 3 ? TOKENS.ganttDowntime : TOKENS.ink900}
            />
          </Box>

          <Box>
            <FieldLabel>Routes · {fam.name} → lines</FieldLabel>
            <Box
              sx={{
                display: 'flex',
                gap: '6px',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <LineToken name={fam.name} tone="teal" />
              <ArrowForwardIcon sx={{ fontSize: 14, color: TOKENS.ink300 }} />
              {s.lines.map((l) => (
                <LineToken
                  key={l}
                  name={l}
                  tone="blue"
                  removable
                  onRemove={() =>
                    update({ lines: s.lines.filter((x) => x !== l) })
                  }
                />
              ))}
              {s.routingId != null && (
                <AddLineMenu
                  avail={avail}
                  onAdd={(l) => update({ lines: [...s.lines, l] })}
                />
              )}
            </Box>
          </Box>

          <PanelCard
            title={`Free-basket pool — ${fam.name}`}
            icon={
              <WaterDropIcon sx={{ fontSize: 15, color: TOKENS.blue600 }} />
            }
            right={
              <CapMeter
                used={fam.cap - Math.round(mn)}
                cap={fam.cap}
                label="peak in use"
                width={120}
              />
            }
          >
            <DayHeader />
            <Box sx={{ position: 'relative' }}>
              <PoolSpark data={pool} cap={fam.cap} height={72} floor={3} />
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  right: 2,
                  top: 2,
                  fontSize: 9,
                  color: mn <= 3 ? TOKENS.ganttDowntime : TOKENS.ink400,
                  fontFamily: MONO,
                  fontWeight: 700,
                }}
              >
                min free = {mn}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                mt: '9px',
              }}
            >
              <Box
                sx={{
                  width: 52,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: TOKENS.ink500,
                  textAlign: 'right',
                }}
              >
                Cycles
              </Box>
              <Box sx={{ flex: 1 }}>
                <WeekTrack height={16}>
                  {cycles.map((c, j) => (
                    <Band
                      key={j}
                      start={c[0]}
                      end={c[1]}
                      color={TOKENS.brandTeal}
                      label="lock"
                      hatch
                    />
                  ))}
                </WeekTrack>
              </Box>
            </Box>
          </PanelCard>
        </Box>
      )
    }
  } else {
    list = resVMs.map((r) => (
      <ListItemCard
        key={r.id}
        on={r.id === effSelId}
        onClick={() => pick(r.id)}
        icon={
          r.resource === 'Dicer' || r.resource === 'Caramelizer' ? (
            <BlenderIcon sx={{ fontSize: 15 }} />
          ) : (
            <RuleIcon sx={{ fontSize: 15 }} />
          )
        }
        name={r.resource}
        meta={`${r.amount} ${r.uom} · ${r.scope}`}
      />
    ))
    const r: ResVM | null =
      draft != null && 'resource' in draft ? (draft as ResVM) : null
    if (r) {
      const usage = resUsage(String(r.id) + r.resource, r.amount)
      const peak = usage.length ? Math.max(...usage.map((b) => b.used)) : 0
      footerNote = infoNote(
        `Hard ceiling — runs cannot exceed ${r.amount} ${r.uom} in any hour`
      )
      detail = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '14px',
              flexWrap: 'wrap',
            }}
          >
            <Stepper
              label={`Capacity (${r.uom})`}
              value={r.amount}
              onDec={() => update({ amount: Math.max(1, r.amount - 5) })}
              onInc={() => update({ amount: r.amount + 5 })}
            />
            <Box>
              <FieldLabel>Scope</FieldLabel>
              <Box
                sx={{
                  height: 28,
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: '11px',
                  border: `1px solid ${TOKENS.line300}`,
                  borderRadius: '3px',
                  background: '#fff',
                  fontSize: 12.5,
                  color: TOKENS.ink700,
                }}
              >
                {r.scope}
              </Box>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Stat label="Consumers" value={r.consumers} unit="products" />
            <Stat
              label="Peak load"
              value={r.amount ? Math.round((peak / r.amount) * 100) : 0}
              unit="%"
              tone={peak > r.amount ? TOKENS.ganttDowntime : TOKENS.ink900}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                p: '5px 10px',
                borderRadius: '3px',
                border: `1px solid ${TOKENS.line300}`,
                background: '#fff',
                fontSize: 11.5,
                fontWeight: 600,
                color: TOKENS.ink500,
              }}
            >
              <WarehouseIcon sx={{ fontSize: 14, color: TOKENS.ink400 }} />
              Hall ownership — off
            </Box>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                p: '5px 10px',
                borderRadius: '3px',
                border: `1px solid ${TOKENS.line300}`,
                background: '#fff',
                fontSize: 11.5,
                fontWeight: 600,
                color: TOKENS.ink500,
              }}
            >
              <CategoryIcon sx={{ fontSize: 14, color: TOKENS.ink400 }} />
              Usage-group dominance — off
            </Box>
          </Box>

          <PanelCard
            title="Hourly consumption vs capacity"
            icon={<BarChartIcon sx={{ fontSize: 15, color: TOKENS.blue600 }} />}
            right={
              <Box
                component="span"
                sx={{ fontFamily: MONO, fontSize: 11, color: TOKENS.ink500 }}
              >
                cap {r.amount} {r.uom}
              </Box>
            }
          >
            <DayHeader />
            <WeekTrack height={40}>
              {usage.map((b, j) => {
                const ratio = r.amount ? Math.min(1.15, b.used / r.amount) : 0
                const over = b.used > r.amount
                return (
                  <Box
                    key={j}
                    title={`${b.used} ${r.uom}`}
                    sx={{
                      position: 'absolute',
                      left: `${pct(b.start)}%`,
                      width: `${pct(b.end - b.start)}%`,
                      bottom: 0,
                      height: `${Math.min(100, ratio * 100)}%`,
                      background: over
                        ? TOKENS.ganttDowntime
                        : TOKENS.ganttProduction,
                      opacity: 0.85,
                      borderRight: `1px solid ${TOKENS.surface0}`,
                    }}
                  />
                )
              })}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  height: '1px',
                  background: TOKENS.ganttDowntime,
                  opacity: 0.6,
                }}
              />
            </WeekTrack>
          </PanelCard>
        </Box>
      )
    }
  }

  const meta = TAB_META[tab]

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        background: TOKENS.surface0,
        position: 'relative',
      }}
    >
      {/* header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          p: '12px 16px',
          borderBottom: `1px solid ${TOKENS.line200}`,
          flexShrink: 0,
        }}
      >
        <Box>
          <Box sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.ink900 }}>
            {meta.title}
          </Box>
          <Box sx={{ fontSize: 11.5, color: TOKENS.ink400 }}>{meta.sub}</Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        <RuleTabs tabs={tabs} active={tab} onPick={setTab} />
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* master list */}
        <Box
          sx={{
            width: 258,
            minWidth: 258,
            borderRight: `1px solid ${TOKENS.line200}`,
            background: TOKENS.surface50,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: '10px 12px 6px',
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                color: TOKENS.ink400,
              }}
            >
              {meta.listTitle}
            </Box>
            <Box
              component="button"
              onClick={() => toast('Add rows in the source data table for now')}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                border: 'none',
                background: 'transparent',
                color: TOKENS.brandTeal,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'inherit',
                '& svg': { fontSize: 14 },
              }}
            >
              <AddIcon />
              New
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: '0 8px 8px' }}>{list}</Box>
        </Box>

        {/* detail editor */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1, overflowY: 'auto', p: '16px 18px' }}>
            {detail || (
              <Box sx={{ color: TOKENS.ink400, fontSize: 13, p: 2 }}>
                {activeList.length
                  ? 'Select a rule from the list to edit it.'
                  : 'No rules of this type yet. Add rows in the source data table.'}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              p: '10px 18px',
              borderTop: `1px solid ${TOKENS.line200}`,
              background: TOKENS.surface50,
              flexShrink: 0,
            }}
          >
            {footerNote}
            <Box sx={{ flex: 1 }} />
            <Box
              component="button"
              onClick={handleCancel}
              disabled={!draft || isSaving}
              sx={{
                border: `1px solid ${TOKENS.line300}`,
                background: '#fff',
                color: TOKENS.ink600,
                borderRadius: '3px',
                px: '12px',
                py: '6px',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                '&:disabled': { opacity: 0.5, cursor: 'default' },
              }}
            >
              Cancel
            </Box>
            <Box
              component="button"
              onClick={handleSave}
              disabled={!draft || isSaving}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                border: `1px solid ${TOKENS.brandTeal}`,
                background: TOKENS.brandTeal,
                color: '#fff',
                borderRadius: '3px',
                px: '12px',
                py: '6px',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                '& svg': { fontSize: 15 },
                '&:disabled': { opacity: 0.5, cursor: 'default' },
              }}
            >
              <SaveIcon />
              {isSaving ? 'Saving…' : 'Save'}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

// ---- small local components ------------------------------------------------

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: 10.5,
      color: TOKENS.ink400,
    }}
  >
    <Box
      component="span"
      sx={{ width: 10, height: 10, borderRadius: '2px', background: color }}
    />
    {label}
  </Box>
)

const ListItemCard = ({
  icon,
  name,
  badge,
  badgeColor,
  meta,
  spark,
  on,
  onClick,
}: {
  icon: React.ReactNode
  name: string
  badge?: string
  badgeColor?: string
  meta: string
  spark?: React.ReactNode
  on: boolean
  onClick: () => void
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      p: '9px 10px',
      mb: '5px',
      borderRadius: '3px',
      cursor: 'pointer',
      background: on ? TOKENS.surface0 : 'transparent',
      border: on ? `1px solid ${TOKENS.brandTeal}` : '1px solid transparent',
      boxShadow: on ? '0 1px 2px rgba(14,27,34,.06)' : 'none',
      '& svg': { color: on ? TOKENS.brandTeal : TOKENS.ink400 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
      {icon}
      <Box
        component="span"
        sx={{
          flex: 1,
          fontSize: 12.5,
          fontWeight: on ? 700 : 600,
          color: on ? TOKENS.ink900 : TOKENS.ink700,
        }}
      >
        {name}
      </Box>
      {badge && (
        <Box
          component="span"
          sx={{
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            px: '6px',
            py: '1px',
            borderRadius: '9px',
            color: badgeColor,
            border: `1px solid ${badgeColor}`,
          }}
        >
          {badge}
        </Box>
      )}
    </Box>
    <Box
      component="span"
      sx={{ fontSize: 10.5, color: TOKENS.ink400, fontFamily: MONO }}
    >
      {meta}
    </Box>
    {spark}
  </Box>
)
